import { dayNumber } from './calculations';
import { eventCatalog, eventCatalogV2, eventCatalogV3 } from './catalog.generated';

type Schema = {
  type?: string;
  format?: string;
  const?: unknown;
  allOf?: readonly Schema[];
  if?: Schema;
  then?: Schema;
  else?: Schema;
  required?: readonly string[];
  properties?: Record<string, Schema>;
  additionalProperties?: boolean;
  anyOf?: readonly Schema[];
  enum?: readonly unknown[];
  items?: Schema;
  minimum?: number;
  maximum?: number;
  multipleOf?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  minProperties?: number;
};
/** Implements only the closed subset used by the versioned catalog; server remains authoritative. */
export function validateSchema(schema: Schema, value: unknown, path = 'payload'): string[] {
  const errors: string[] = [];
  if (Object.hasOwn(schema, 'const') && schema.const !== value) errors.push(`${path}: unexpected value`);
  if (schema.enum && !schema.enum.includes(value)) errors.push(`${path}: choose a supported value`);
  if (schema.type === 'object' || schema.required || schema.properties) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path}: expected object`];
    const record = value as Record<string, unknown>;
    for (const key of schema.required ?? []) if (record[key] === undefined) errors.push(`${path}.${key}: required`);
    if (schema.minProperties !== undefined && Object.keys(record).length < schema.minProperties)
      errors.push(`${path}: enter a finding`);
    for (const [key, item] of Object.entries(record)) {
      if (schema.properties?.[key]) errors.push(...validateSchema(schema.properties[key], item, `${path}.${key}`));
      else if (schema.additionalProperties === false) errors.push(`${path}.${key}: unsupported field`);
    }
  } else if (schema.type === 'array') {
    if (!Array.isArray(value)) return [`${path}: expected array`];
    if (
      (schema.minItems !== undefined && value.length < schema.minItems) ||
      (schema.maxItems !== undefined && value.length > schema.maxItems)
    )
      errors.push(`${path}: incorrect number of items`);
    if (schema.items)
      value.forEach((v, index) => errors.push(...validateSchema(schema.items!, v, `${path}[${index}]`)));
  } else if (schema.type === 'number' || schema.type === 'integer') {
    if (typeof value !== 'number' || !Number.isFinite(value) || (schema.type === 'integer' && !Number.isInteger(value)))
      return [`${path}: expected finite ${schema.type}`];
    if (
      (schema.minimum !== undefined && value < schema.minimum) ||
      (schema.maximum !== undefined && value > schema.maximum)
    )
      errors.push(`${path}: outside allowed range`);
    if (schema.multipleOf && Math.abs(value / schema.multipleOf - Math.round(value / schema.multipleOf)) > 1e-8)
      errors.push(`${path}: invalid increment`);
  } else if (schema.type === 'string') {
    if (typeof value !== 'string') return [`${path}: expected text`];
    if (
      schema.format === 'date-time' &&
      (!/(Z|[+-]\d{2}:\d{2})$/.test(value) ||
        !Number.isFinite(Date.parse(value)) ||
        dayNumber(value.slice(0, 10)) === null)
    )
      errors.push(`${path}: expected a valid timestamp with timezone`);
    if (schema.format === 'date' && dayNumber(value) === null) errors.push(`${path}: invalid calendar date`);
    if (
      (schema.minLength !== undefined && value.trim().length < schema.minLength) ||
      (schema.maxLength !== undefined && value.length > schema.maxLength)
    )
      errors.push(`${path}: invalid text length`);
  } else if (schema.type === 'boolean' && typeof value !== 'boolean') errors.push(`${path}: expected yes/no`);
  if (schema.anyOf && !schema.anyOf.some((option) => validateSchema(option, value, path).length === 0))
    errors.push(`${path}: required alternative fields missing`);
  if (schema.allOf) for (const part of schema.allOf) errors.push(...validateSchema(part, value, path));
  if (schema.if) {
    const branch = validateSchema(schema.if, value, path).length === 0 ? schema.then : schema.else;
    if (branch) errors.push(...validateSchema(branch, value, path));
  }
  return errors;
}
export function validatePayload(eventType: string, payload: unknown, schemaVersion = 1): string[] {
  const catalog =
    schemaVersion === 1
      ? eventCatalog
      : schemaVersion === 2
        ? eventCatalogV2
        : schemaVersion === 3
          ? eventCatalogV3
          : null;
  if (!catalog) return ['Unsupported schema version'];
  if (!Object.hasOwn(catalog, eventType)) return ['Unsupported event type'];
  const errors = validateSchema((catalog as Record<string, { schema: Schema }>)[eventType].schema, payload);
  if (!payload || typeof payload !== 'object') return errors;
  const p = payload as Record<string, unknown>;
  for (const [low, high] of [
    ['low_bpm', 'high_bpm'],
    ['freq_min_low', 'freq_min_high'],
    ['duration_s_low', 'duration_s_high'],
  ]) {
    if (typeof p[low] === 'number' && typeof p[high] === 'number' && p[low] > p[high])
      errors.push(`${low}: cannot exceed ${high}`);
  }
  if (
    eventType === 'VITALS.BP' &&
    typeof p.systolic === 'number' &&
    typeof p.diastolic === 'number' &&
    p.systolic <= p.diastolic
  )
    errors.push('Systolic must exceed diastolic');
  return errors;
}
